// NOTE: All data fetching for SSR/SSG must be done in the server component/page/layout.
// This provider hydrates the singleton Zustand stores with SSR data passed as props.
// See: https://zustand.docs.pmnd.rs/guides/nextjs

"use client";

import { useListDataStore, Pagination } from "@/stores/search/list-store";
import { useLocationsStore, Location } from "@/stores/search/locations-store";
import { useMapDataStore } from "@/stores/search/map-store";
import type { Bounds } from "@/types/map";
import type { ListItem, MapItem, MapItemWithCoodId } from "@/types/search";
import type { ReactNode } from "react";
import { useRef } from "react";
export interface SearchPageProviderProps {
  children: ReactNode;
  slug?: string; // URL slug for SSR-friendly filter initialization
  initialListData?: ListItem[];
  initialPagination?: Pagination;
  initialMapData?: MapItem[] | MapItemWithCoodId[];
  initialBounds?: Bounds | null;
  initialMapCenter?: {
    lat: number;
    lng: number;
  };
  initialMapZoom?: number | null;
  mapId?: string;
  initialLocations?: Location[];
  initialTotalCount?: number | null;
}

/**
 * Hydrates singleton search page stores with SSR data.
 * No React Contexts are created — stores are global singletons.
 */
export function SearchPageProvider({
  children,
  initialListData,
  initialPagination,
  initialMapData,
  initialBounds,
  initialMapCenter,
  initialMapZoom,
  mapId,
  initialLocations,
  initialTotalCount
}: SearchPageProviderProps) {
  // Hydrate stores once on first render (useRef ensures this runs only once)
  const hydrated = useRef(false);
  if (!hydrated.current) {
    hydrated.current = true;

    // Hydrate list data store
    if (initialListData || initialPagination) {
      useListDataStore.setState({
        ...(initialListData ? {
          listData: initialListData
        } : {}),
        ...(initialPagination ? {
          pagination: initialPagination
        } : {})
      });
    }

    // Hydrate map data store
    if (initialMapData || initialBounds || initialMapCenter || initialMapZoom || mapId || initialTotalCount) {
      // Convert MapItem[] to MapItemWithCoodId[] if needed
      const convertedMapData = initialMapData?.map((item: MapItem | MapItemWithCoodId) => {
        if (typeof item._id === "string") {
          return {
            ...item,
            _id: item._id.split(",").map(Number)
          } as MapItemWithCoodId;
        }
        return item as MapItemWithCoodId;
      });
      useMapDataStore.setState({
        ...(convertedMapData ? {
          mapData: convertedMapData,
          initialMapData: convertedMapData
        } : {}),
        ...(initialBounds !== undefined ? {
          bounds: initialBounds
        } : {}),
        ...(initialMapCenter ? {
          mapCenter: initialMapCenter
        } : {}),
        ...(initialMapZoom !== undefined ? {
          mapZoom: initialMapZoom
        } : {}),
        ...(mapId ? {
          mapId
        } : {}),
        ...(initialTotalCount !== undefined ? {
          totalCountWithoutBounds: initialTotalCount
        } : {})
      });
    }

    // Hydrate locations store
    if (initialLocations && initialLocations.length > 0) {
      useLocationsStore.setState({
        locations: initialLocations
      });
    }
  }
  return <>{children}</>;
}